import { LitElement, html, nothing, type PropertyValues, type TemplateResult } from "lit";
import {
  DEFAULT_PARAMETERS,
  FLOOR_AREA_MAX,
  FLOOR_AREA_MIN,
  HALL_HEIGHTS,
  SEASONS_PER_YEAR,
  WHEELSET_MIN,
  calculate,
  floorAreaMaxForHeight,
  formatDecimal,
  formatNumber,
  formatResult,
  isHallHeight,
  parseParameters,
  syncFloorAreaFromWheelsets,
  syncWheelsetsFromFloorArea,
  wheelsetMaxForHeight,
  type CalculatorInput,
  type CalculatorParameters,
  type HallHeight,
} from "@schulte/calculator";
import { read as readTouchpoints } from "@schulte/touchpoint-tracker";
import { buildLeadSubmission, submitLead, type SubmitOutcome } from "./submission.js";
import { styles } from "./styles.js";
import { variantStyles } from "./variant-styles.js";
import { layoutFromScriptUrl, parseLayout } from "./layouts.js";

type Status = "idle" | "submitting" | "success" | "error";

interface ContactDraft {
  name: string;
  company: string;
  email: string;
  phone: string;
}

const EMPTY_CONTACT: ContactDraft = { name: "", company: "", email: "", phone: "" };

const HEIGHT_LABELS: Record<HallHeight, { title: string; hint: string }> = {
  low: { title: "1-stöckig", hint: "< 3 m" },
  mid: { title: "2-stöckig", hint: "3 – 5 m" },
  high: { title: "3-stöckig", hint: "> 5 m" },
};

// Floor area is the first question, so until the visitor touches the wheelset slider a change of hall height
// keeps the area and recalculates the wheelsets. Derived through the same sync the API applies, so an
// untouched form submits exactly the numbers the visitor saw (58 m², two stories: 204 wheel sets).
const DEFAULT_INPUT: CalculatorInput = syncWheelsetsFromFloorArea({
  wheelsets: 0,
  floorArea: 58,
  height: "mid",
  lastChanged: "floorArea",
});

const ERROR_MESSAGES: Record<Exclude<SubmitOutcome, { ok: true }>["kind"], string> = {
  validation: "Bitte prüfen Sie Ihre Angaben und versuchen Sie es erneut.",
  rate_limited: "Zu viele Anfragen. Bitte versuchen Sie es in einigen Minuten erneut.",
  server: "Die Anfrage konnte gerade nicht gesendet werden. Bitte versuchen Sie es später erneut.",
  network: "Keine Verbindung zum Server. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.",
};

/**
 * <schulte-roi-calculator>
 *
 * Attributes:
 *   layout                  classic (default), v1, v2, or v3; also selected by ?layout=v2 on the script URL
 *   api-url                 POST endpoint for lead submissions; defaults to /v1/leads on the script's origin
 *   config-url              GET endpoint for the calculator parameters; defaults to /v1/calculator-config on
 *                           the script's origin. Until it answers, the built-in defaults are used.
 *   privacy-url             Link target for the privacy notice
 *   privacy-notice-version  Optional version string stored with each lead (default "unversioned")
 *   consent-checkbox        When present, a required consent checkbox is shown
 *
 * Events (bubble, composed): schulte-roi:interact, schulte-roi:cap-reached,
 * schulte-roi:submit, schulte-roi:success, schulte-roi:error
 */
export class SchulteRoiCalculator extends LitElement {
  static override styles = [styles, variantStyles];

  static override properties = {
    layout: { type: String, reflect: true },
    apiUrl: { type: String, attribute: "api-url" },
    configUrl: { type: String, attribute: "config-url" },
    privacyUrl: { type: String, attribute: "privacy-url" },
    privacyNoticeVersion: { type: String, attribute: "privacy-notice-version" },
    consentCheckbox: { type: Boolean, attribute: "consent-checkbox" },
    input: { state: true },
    parameters: { state: true },
    contact: { state: true },
    status: { state: true },
    errorMessage: { state: true },
  };

  declare apiUrl: string;
  declare layout: string;
  declare configUrl: string;
  declare privacyUrl: string;
  declare privacyNoticeVersion: string;
  declare consentCheckbox: boolean;

  private declare input: CalculatorInput;
  /** Commercial parameters, served by the API and edited in the admin backend. */
  private declare parameters: CalculatorParameters;
  private declare contact: ContactDraft;
  private declare status: Status;
  private declare errorMessage: string;

  private honeypot = "";
  private capReached = false;
  private idempotencyKey = newId();
  private interactiveSince = 0;
  private readonly uid = `sroi-${Math.random().toString(36).slice(2, 8)}`;

  constructor() {
    super();
    this.layout = layoutFromScriptUrl(import.meta.url);
    this.apiUrl = defaultUrl("/v1/leads");
    this.configUrl = defaultUrl("/v1/calculator-config");
    this.privacyUrl = "";
    this.privacyNoticeVersion = "unversioned";
    this.consentCheckbox = false;
    this.input = { ...DEFAULT_INPUT };
    this.parameters = { ...DEFAULT_PARAMETERS };
    this.contact = { ...EMPTY_CONTACT };
    this.status = "idle";
    this.errorMessage = "";
  }

  override connectedCallback(): void {
    super.connectedCallback();
    void this.loadParameters();
  }

  protected override firstUpdated(_changed: PropertyValues): void {
    this.interactiveSince = performance.now();
  }

  /** Replaces the built-in defaults with the set sales saved in the backend. Failures keep the defaults. */
  private async loadParameters(): Promise<void> {
    if (!this.configUrl || typeof fetch !== "function") return;
    try {
      const response = await fetch(this.configUrl, { headers: { accept: "application/json" } });
      if (!response.ok) return;
      const body = (await response.json()) as { parameters?: unknown };
      const parameters = parseParameters(body?.parameters);
      if (parameters) this.parameters = parameters;
    } catch {
      // Offline or blocked: the defaults are close enough; the API recalculates every lead anyway.
    }
  }

  private result() {
    return calculate(this.input, this.parameters);
  }

  private fieldId(suffix: string): string {
    return `${this.uid}-${suffix}`;
  }

  private emit(name: string, detail: Record<string, unknown> = {}): void {
    this.dispatchEvent(new CustomEvent(`schulte-roi:${name}`, { detail, bubbles: true, composed: true }));
  }

  private onWheelsetsInput(e: Event): void {
    const value = Number((e.target as HTMLInputElement).value);
    this.input = syncFloorAreaFromWheelsets({ ...this.input, wheelsets: value });
    this.emit("interact", { field: "wheelsets" });
  }

  private onFloorAreaInput(e: Event): void {
    const value = Number((e.target as HTMLInputElement).value);
    this.input = syncWheelsetsFromFloorArea({ ...this.input, floorArea: value });
    this.emit("interact", { field: "floorArea" });
  }

  /** Typed value committed (blur or Enter). Invalid input falls back to the current value. */
  private onWheelsetsCommit(e: Event): void {
    const field = e.target as HTMLInputElement;
    const value = Number(field.value);
    if (Number.isFinite(value) && field.value.trim() !== "") {
      this.input = syncFloorAreaFromWheelsets({ ...this.input, wheelsets: value });
      this.emit("interact", { field: "wheelsets", typed: true });
    }
    // Lit only writes .value when the bound value changes, so a clamped or rejected entry has to be reset by hand.
    field.value = String(this.input.wheelsets);
  }

  private onFloorAreaCommit(e: Event): void {
    const field = e.target as HTMLInputElement;
    const value = Number(field.value);
    if (Number.isFinite(value) && field.value.trim() !== "") {
      this.input = syncWheelsetsFromFloorArea({ ...this.input, floorArea: value });
      this.emit("interact", { field: "floorArea", typed: true });
    }
    field.value = String(this.input.floorArea);
  }

  private blurOnEnter(e: KeyboardEvent): void {
    if (e.key === "Enter") {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
  }

  private focusContactForm(): void {
    const name = this.renderRoot.querySelector<HTMLInputElement>('input[name="name"]');
    if (!name) return;
    name.scrollIntoView({ behavior: "smooth", block: "center" });
    name.focus({ preventScroll: true });
  }

  protected override updated(changed: PropertyValues): void {
    if (!changed.has("input")) return;
    const capped = this.result().isCappedEstimate;
    if (capped && !this.capReached) this.emit("cap-reached", { floorArea: this.input.floorArea, height: this.input.height });
    this.capReached = capped;
  }

  private onHeightSelect(height: HallHeight): void {
    if (!isHallHeight(height) || height === this.input.height) return;
    const next = { ...this.input, height };
    this.input = next.lastChanged === "floorArea" ? syncWheelsetsFromFloorArea(next) : syncFloorAreaFromWheelsets(next);
    this.emit("interact", { field: "height" });
  }

  private onContactInput(field: keyof ContactDraft, e: Event): void {
    this.contact = { ...this.contact, [field]: (e.target as HTMLInputElement).value };
  }

  private async onSubmit(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    if (this.status === "submitting") return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    if (!this.apiUrl) {
      this.status = "error";
      this.errorMessage = ERROR_MESSAGES.server;
      console.error("<schulte-roi-calculator>: api-url attribute is missing");
      return;
    }

    const result = this.result();
    const body = buildLeadSubmission({
      idempotencyKey: this.idempotencyKey,
      contact: this.contact,
      input: this.input,
      result,
      pageUrl: window.location.href,
      locale: document.documentElement.lang || navigator.language || "de",
      privacyNoticeVersion: this.privacyNoticeVersion,
      clientVersion: __CLIENT_VERSION__,
      fillTimeMs: performance.now() - this.interactiveSince,
      honeypot: this.honeypot,
      touchpoints: safeReadTouchpoints(),
    });

    this.status = "submitting";
    this.errorMessage = "";
    this.emit("submit", { wheelsets: this.input.wheelsets, height: this.input.height });

    const outcome = await submitLead(this.apiUrl, body);
    if (outcome.ok) {
      this.status = "success";
      this.contact = { ...EMPTY_CONTACT };
      this.idempotencyKey = newId();
      // Use the submitted snapshot: sliders may change while the request is in flight.
      this.emit("success", {
        id: outcome.id,
        wheelsets: body.calculator.input.wheelsets,
        floorArea: body.calculator.input.floorArea,
        height: body.calculator.input.height,
        isCappedEstimate: result.isCappedEstimate,
        investment: result.investment,
        currency: "EUR",
      });
    } else {
      this.status = "error";
      this.errorMessage = ERROR_MESSAGES[outcome.kind];
      // Keep the same idempotency key so a retry cannot create a duplicate.
      this.emit("error", { kind: outcome.kind });
    }
  }

  protected override render(): TemplateResult {
    const result = this.result();
    const formatted = formatResult(result);
    const p = this.parameters;
    const areaMax = floorAreaMaxForHeight(this.input.height);
    const wheelsetMax = wheelsetMaxForHeight(this.input.height);
    const capped = result.isCappedEstimate;
    const layout = parseLayout(this.layout);
    const compact = layout !== "classic";

    const content = html`
      <div class="card inputs">
        <fieldset>
          <legend><span class="question-number" aria-hidden="true">1</span>Verfügbare Stellfläche?</legend>
          <div class="slider-wrap">
            <div class="slider-value">
              <label class="visually-hidden" for=${this.fieldId("area-value")}>Stellfläche in Quadratmetern eingeben</label>
              <input
                id=${this.fieldId("area-value")}
                class="value-input"
                type="number"
                inputmode="numeric"
                min=${FLOOR_AREA_MIN}
                max=${areaMax}
                step="1"
                .value=${String(this.input.floorArea)}
                @change=${this.onFloorAreaCommit}
                @keydown=${this.blurOnEnter}
              />
              <small>m²${capped && !compact ? " · Maximum" : ""}</small>
            </div>
            <label class="visually-hidden" for=${this.fieldId("area")}>Verfügbare Stellfläche in Quadratmetern</label>
            <input
              id=${this.fieldId("area")}
              type="range"
              min=${FLOOR_AREA_MIN}
              max=${areaMax}
              step="1"
              .value=${String(this.input.floorArea)}
              aria-valuetext=${`${formatNumber(this.input.floorArea)} Quadratmeter`}
              @input=${this.onFloorAreaInput}
            />
            <div class="slider-ticks" aria-hidden="true">${ticks(FLOOR_AREA_MIN, areaMax, "+")}</div>
          </div>
        </fieldset>

        <fieldset>
          <legend id=${this.fieldId("height-label")}>
            <span class="question-number" aria-hidden="true">2</span>Verfügbare Hallenhöhe?
          </legend>
          <div class="options" role="group" aria-labelledby=${this.fieldId("height-label")}>
            ${HALL_HEIGHTS.map(
              (h) => html`
                <button
                  type="button"
                  class="option-btn"
                  aria-pressed=${this.input.height === h ? "true" : "false"}
                  @click=${() => this.onHeightSelect(h)}
                >
                  ${HEIGHT_LABELS[h].title}<small>${HEIGHT_LABELS[h].hint}</small>
                </button>
              `,
            )}
          </div>
        </fieldset>

        <fieldset>
          <legend><span class="question-number" aria-hidden="true">3</span>Wie viele Radsätze lagern Sie pro Saison?</legend>
          <div class="slider-wrap">
            <div class="slider-value">
              <label class="visually-hidden" for=${this.fieldId("wheelsets-value")}>Radsätze pro Saison eingeben</label>
              <input
                id=${this.fieldId("wheelsets-value")}
                class="value-input"
                type="number"
                inputmode="numeric"
                min=${WHEELSET_MIN}
                max=${wheelsetMax}
                step="1"
                .value=${String(this.input.wheelsets)}
                @change=${this.onWheelsetsCommit}
                @keydown=${this.blurOnEnter}
              />
              <small>Radsätze${capped && !compact ? " · Maximum" : ""}</small>
            </div>
            <label class="visually-hidden" for=${this.fieldId("wheelsets")}>Radsätze pro Saison</label>
            <input
              id=${this.fieldId("wheelsets")}
              type="range"
              min=${WHEELSET_MIN}
              max=${wheelsetMax}
              step="1"
              .value=${String(this.input.wheelsets)}
              aria-valuetext=${`${formatNumber(this.input.wheelsets)} Radsätze`}
              @input=${this.onWheelsetsInput}
            />
            <div class="slider-ticks" aria-hidden="true">${ticks(WHEELSET_MIN, wheelsetMax, "+")}</div>
          </div>
        </fieldset>

        ${capped && layout !== "v3" ? this.renderCapNotice(layout === "v2") : nothing}
      </div>

      <section class="result" aria-live="polite" aria-atomic="true">
        <div class="result-label">Ihr Bedarf</div>
        <div class="result-main">
          Sie brauchen <strong>${formatted.rackCount} Reifenregale</strong>
        </div>
        <div class="result-box">
          Investition*
          <span class="amount">${formatted.investment}</span>
        </div>
        <div class="result-box roi">
          ${formatted.roiLabel}
          <span class="amount">${formatted.roiPercent}</span>
          <span class="result-secondary">Absoluter Return: ${formatted.absoluteReturn}</span>
          <small class="result-hint">
            ${p.roiYears * SEASONS_PER_YEAR}× Saisonumsatz (${p.roiYears} Jahre · ${SEASONS_PER_YEAR} Saisons) − Investition*
          </small>
        </div>
        <div class="result-meta">
          <div class="result-meta-item"><span>Stellfläche</span><strong>${formatted.floorArea}</strong></div>
          <div class="result-meta-item"><span>Ausführung</span><strong>${formatted.stories}</strong></div>
          <div class="result-meta-item"><span>Ø Umsatz/Saison</span><strong>${formatted.revenuePerSeason}</strong></div>
          <div class="result-meta-item"><span>Amortisation</span><strong>${formatted.payback}</strong></div>
        </div>
        <div class="result-disclaimer">
          * ${formatDecimal(p.priceBaseRack)} € Grundregal + ${formatDecimal(p.priceAddOnRack)} € je Anbauregal (zzgl. MwSt.)
        </div>
      </section>

      <div class="card form-section">
        <h2>Ihre individuelle Planung anfordern</h2>
        ${capped
          ? html`
              <p class="subtitle">Wir melden uns innerhalb von 24 Stunden und stimmen einen Termin vor Ort ab.</p>
            `
          : html`
              <p class="subtitle">Wir melden uns innerhalb von 24 Stunden bei Ihnen.</p>
            `}
        ${this.status === "success" ? this.renderSuccess() : html`${compact ? this.renderRequestSummary(capped) : nothing}${this.renderForm()}`}
      </div>
      ${capped && layout === "v3" ? this.renderCapNotice() : nothing}
    `;
    return compact ? html`<div class="calculator-layout" data-layout=${layout}>${content}</div>` : content;
  }

  private renderRequestSummary(capped: boolean): TemplateResult {
    const height = HEIGHT_LABELS[this.input.height];
    return html`
      <div class="request-summary" role="group" aria-label="Ihre Anfrage umfasst">
        <span class="request-label">Ihre Anfrage umfasst:</span>
        <span>${formatNumber(this.input.floorArea)}${capped ? "+" : ""} m²</span>
        <span>${formatNumber(this.input.wheelsets)}${capped ? "+" : ""} Radsätze</span>
        <span>${height.title} <small>Halle ${height.hint}</small></span>
        ${capped ? html`<span class="request-cap">Großprojekt</span>` : nothing}
      </div>
    `;
  }

  private renderCapNotice(collapsible = false): TemplateResult {
    const explanation = html`
      Ab ${formatNumber(FLOOR_AREA_MAX)} m² gehen wir mit Ihnen vor Ort in die Planung: Begehung, Wegeführung
      und Ein- und Auslagerung in der Saisonspitze, Umsetzung in Etappen bei laufendem Betrieb, Konditionen
      nach Menge. Referenzen vergleichbarer Größe zeigen wir Ihnen gern. Die Werte hier sind eine Untergrenze.
    `;
    return html`
      <div class="cap-notice" role="note">
        <strong>So planen wir Ihr Großprojekt</strong>
        ${collapsible
          ? html`<span>Die Werte sind eine Untergrenze.</span><details><summary>Planung im Detail</summary>${explanation}</details>`
          : explanation}
        <button type="button" class="link-btn" @click=${this.focusContactForm}>Großprojekt besprechen ›</button>
      </div>
    `;
  }

  private renderForm(): TemplateResult {
    const submitting = this.status === "submitting";
    return html`
      <form @submit=${this.onSubmit} novalidate>
        <div class="form-row">
          <label for=${this.fieldId("name")}>Name ${this.requiredMark()}</label>
          <input
            id=${this.fieldId("name")}
            name="name"
            type="text"
            required
            maxlength="200"
            autocomplete="name"
            placeholder="Max Mustermann"
            .value=${this.contact.name}
            @input=${(e: Event) => this.onContactInput("name", e)}
          />
        </div>
        <div class="form-row">
          <label for=${this.fieldId("company")}>Unternehmen ${this.requiredMark()}</label>
          <input
            id=${this.fieldId("company")}
            name="company"
            type="text"
            required
            maxlength="200"
            autocomplete="organization"
            placeholder="Mustermann GmbH"
            .value=${this.contact.company}
            @input=${(e: Event) => this.onContactInput("company", e)}
          />
        </div>
        <div class="form-row">
          <label for=${this.fieldId("email")}>E-Mail ${this.requiredMark()}</label>
          <input
            id=${this.fieldId("email")}
            name="email"
            type="email"
            required
            maxlength="254"
            autocomplete="email"
            placeholder="max@mustermann.de"
            .value=${this.contact.email}
            @input=${(e: Event) => this.onContactInput("email", e)}
          />
        </div>
        <div class="form-row">
          <label for=${this.fieldId("phone")}>Telefon <span class="optional">(optional)</span></label>
          <input
            id=${this.fieldId("phone")}
            name="phone"
            type="tel"
            maxlength="40"
            autocomplete="tel"
            inputmode="tel"
            placeholder="+49 30 1234567"
            .value=${this.contact.phone}
            @input=${(e: Event) => this.onContactInput("phone", e)}
          />
        </div>
        <p class="required-hint"><span class="required" aria-hidden="true">*</span> Pflichtfeld</p>

        <div class="honeypot" aria-hidden="true">
          <label for=${this.fieldId("website")}>Website</label>
          <input
            id=${this.fieldId("website")}
            name="website"
            type="text"
            tabindex="-1"
            autocomplete="off"
            @input=${(e: Event) => (this.honeypot = (e.target as HTMLInputElement).value)}
          />
        </div>

        ${this.consentCheckbox
          ? html`
              <label class="consent">
                <input type="checkbox" name="consent" required />
                <span>Ich bin mit der Verarbeitung meiner Angaben zur Bearbeitung meiner Anfrage einverstanden. ${this.renderPrivacyLink()} ${this.requiredMark()}</span>
              </label>
            `
          : html`
              <p class="privacy">
                Mit dem Absenden werden Ihre Angaben zur Bearbeitung Ihrer Anfrage verarbeitet. ${this.renderPrivacyLink()}
              </p>
            `}

        <button type="submit" class="submit-btn" ?disabled=${submitting} aria-busy=${submitting ? "true" : "false"}>
          ${submitting ? "Wird gesendet…" : "Jetzt Ihr Angebot erhalten"}
        </button>

        <div role="status" aria-live="assertive">
          ${this.status === "error" ? html`<div class="status error">${this.errorMessage}</div>` : nothing}
        </div>
      </form>
    `;
  }

  /** The asterisk is decorative; `required` on the input carries the semantics for screen readers. */
  private requiredMark(): TemplateResult {
    return html`<span class="required" aria-hidden="true">*</span>`;
  }

  private renderSuccess(): TemplateResult {
    return html`<div class="status success" role="status">Vielen Dank! Wir melden uns in Kürze bei Ihnen.</div>`;
  }

  private renderPrivacyLink(): TemplateResult | typeof nothing {
    if (!this.privacyUrl) return nothing;
    return html`<a href=${this.privacyUrl} target="_blank" rel="noopener">Datenschutzhinweise</a>`;
  }
}

function ticks(min: number, max: number, lastSuffix: string): TemplateResult[] {
  const steps = [0, 0.25, 0.5, 0.75, 1];
  return steps.map((s, i) => {
    const value = Math.round(min + (max - min) * s);
    return html`<span>${formatNumber(value)}${i === steps.length - 1 ? lastSuffix : ""}</span>`;
  });
}

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  // Fallback for very old browsers: RFC 4122 v4 layout from Math.random.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * The API lives next to this script, on the origin roi-calculator.js was loaded from.
 * Empty outside a browser (tests) or when the script was loaded from a file.
 */
function defaultUrl(path: string): string {
  try {
    const url = new URL(path, import.meta.url);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : "";
  } catch {
    return "";
  }
}

function safeReadTouchpoints() {
  try {
    return readTouchpoints(window.localStorage);
  } catch {
    return null;
  }
}
